class GatedArticle extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    document.addEventListener("click", this.onClick);
    this._ctaTimerId = null;
    this._player = null;
    this._ctaVisible = false;
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onClick);
    this._cleanupCta();
  }

  onClick(e) {
    const trigger = e.target.closest(".play-trailer");
    if (!trigger) return;

    this._waitForModalIframe()
      .then(({ iframe, modalEl }) => {
        if (!window.Vimeo || !iframe) return;

        const player = new window.Vimeo.Player(iframe);
        this._player = player;

        player.setLoop(false).catch(() => {});
        player.setCurrentTime(0).catch(() => {});
        player.play().catch(() => {});

        if (this.dataset.showPostVideoCta === "true") {
          this._setupPostVideoCta(player, modalEl);
        }

        const closeBtn = this._findCloseButton(modalEl);
        if (closeBtn) {
          const onClose = () => {
            player.pause().catch(() => {});
            player.unload().catch(() => {});
            this._cleanupCta();
            closeBtn.removeEventListener("click", onClose);
          };
          closeBtn.addEventListener("click", onClose, { once: true });
        }

        const mo = new MutationObserver(() => {
          if (!document.body.contains(modalEl)) {
            player.unload().catch(() => {});
            this._cleanupCta();
            mo.disconnect();
          }
        });
        mo.observe(document.body, { childList: true, subtree: true });
      })
      .catch(() => {});
  }

  // ----- Post-Video CTA -----

  _setupPostVideoCta(player, modalEl) {
    const ctaEl = modalEl.querySelector(".post-video-cta");
    if (!ctaEl) return;

    this._hideCta(ctaEl);
    this._signupTriggered = false;

    let ctaShown = false;

    // Show tooltip 8 seconds before the video ends
    player.getDuration().then((duration) => {
      const showTime = Math.max(0, duration - 8);

      const onTimeUpdate = (data) => {
        if (!ctaShown && data.seconds >= showTime) {
          ctaShown = true;
          const remaining = Math.max(0, duration - data.seconds);
          this._showCta(ctaEl);
          this._startCtaTimer(ctaEl, modalEl, remaining);
        } else if (ctaShown && !this._signupTriggered && data.seconds < showTime) {
          // User seeked backward — cancel timer and re-arm the trigger
          ctaShown = false;
          this._cancelCtaTimer();
          this._hideCta(ctaEl);
        }
      };
      player.on("timeupdate", onTimeUpdate);
      this._onTimeUpdate = onTimeUpdate;
    });

    // When video ends, immediately open signup modal
    const onEnded = () => {
      if (this._signupTriggered) return;
      this._signupTriggered = true;
      this._cancelCtaTimer();
      this._hideCta(ctaEl);
      this._closeVideoAndOpenSignup(modalEl);
    };
    player.on("ended", onEnded);
    this._onEnded = onEnded;

    // CTA button: end video early, open signup
    const ctaButton = ctaEl.querySelector(".post-video-cta__button");
    if (ctaButton) {
      const onCtaClick = () => {
        if (this._signupTriggered) return;
        this._signupTriggered = true;
        this._cancelCtaTimer();
        this._hideCta(ctaEl);
        player.off("ended", onEnded);
        player.pause().catch(() => {});
        this._closeVideoAndOpenSignup(modalEl);
      };
      ctaButton.addEventListener("click", onCtaClick);
      this._ctaButtonHandler = { el: ctaButton, fn: onCtaClick };
    }

    // Close button: hide tooltip, prevent auto-modal
    const closeButton = ctaEl.querySelector(".post-video-cta__close");
    if (closeButton) {
      const onCloseClick = () => {
        this._cancelCtaTimer();
        this._hideCta(ctaEl);
        player.off("ended", onEnded);
        this._onEnded = null;
      };
      closeButton.addEventListener("click", onCloseClick);
      this._ctaCloseHandler = { el: closeButton, fn: onCloseClick };
    }
  }

  _showCta(ctaEl) {
    if (window.innerWidth < 768 && !ctaEl._movedToBody) {
      ctaEl._originalParent = ctaEl.parentElement;
      ctaEl._originalNextSibling = ctaEl.nextSibling;
      document.body.appendChild(ctaEl);
      ctaEl.style.zIndex = "2147483642";
      ctaEl._movedToBody = true;
    }
    ctaEl.classList.remove(
      "opacity-0",
      "invisible",
      "pointer-events-none",
      "translate-y-full",
      "md:translate-y-[10px]"
    );
    ctaEl.classList.add(
      "opacity-100",
      "visible",
      "pointer-events-auto",
      "translate-y-0",
      "md:translate-y-0"
    );
    ctaEl.setAttribute("aria-hidden", "false");
    this._ctaVisible = true;
  }

  _hideCta(ctaEl) {
    if (!ctaEl) return;
    if (ctaEl._movedToBody && ctaEl._originalParent) {
      if (ctaEl._originalNextSibling) {
        ctaEl._originalParent.insertBefore(ctaEl, ctaEl._originalNextSibling);
      } else {
        ctaEl._originalParent.appendChild(ctaEl);
      }
      ctaEl.style.zIndex = "";
      ctaEl._movedToBody = false;
    }
    ctaEl.classList.remove(
      "opacity-100",
      "visible",
      "pointer-events-auto",
      "translate-y-0",
      "md:translate-y-0"
    );
    ctaEl.classList.add(
      "opacity-0",
      "invisible",
      "pointer-events-none",
      "translate-y-full",
      "md:translate-y-[10px]"
    );
    ctaEl.setAttribute("aria-hidden", "true");
    this._ctaVisible = false;

    const timerBar = ctaEl.querySelector(".post-video-cta__timer-bar");
    if (timerBar) {
      timerBar.style.animation = "";
    }
  }

  _startCtaTimer(ctaEl, modalEl, durationSec = 8) {
    const timerBar = ctaEl.querySelector(".post-video-cta__timer-bar");
    if (timerBar) {
      timerBar.style.animation = "";
      void timerBar.offsetWidth;
      timerBar.style.animation =
        "post-video-timer " + durationSec + "s linear forwards";
    }

    // Safety net: if the ended event hasn't fired by now, force open
    this._ctaTimerId = setTimeout(() => {
      this._ctaTimerId = null;
      if (this._signupTriggered) return;
      this._signupTriggered = true;
      this._hideCta(ctaEl);
      this._closeVideoAndOpenSignup(modalEl);
    }, durationSec * 1000);
  }

  _cancelCtaTimer() {
    if (this._ctaTimerId) {
      clearTimeout(this._ctaTimerId);
      this._ctaTimerId = null;
    }
  }

  _closeVideoAndOpenSignup(modalEl) {
    const teddyModalEl = modalEl.closest("teddy-modal");
    if (teddyModalEl && typeof teddyModalEl.closeModal === "function") {
      teddyModalEl.closeModal();
    } else {
      modalEl.classList.remove("active");
      if (this._player) {
        this._player.pause().catch(() => {});
        this._player.setCurrentTime(0).catch(() => {});
      }
    }

    setTimeout(() => {
      const $signupModal = document.querySelector(".newsletter-signup-modal");
      if (!$signupModal) return;

      const signupComponent = $signupModal.querySelector("signup-modal");
      if (signupComponent) {
        if (this.dataset.redirectUrl) {
          signupComponent.redirectUrl = this.dataset.redirectUrl;
        }
        if (this.dataset.emailSource) {
          signupComponent.emailSource = this.dataset.emailSource;
        }
        if (this.dataset.smsSource) {
          signupComponent.smsSource = this.dataset.smsSource;
        }
      }

      const titleEl = $signupModal.querySelector("[data-signup-title]");
      if (titleEl && this.dataset.signupModalTitle) {
        if (!this._originalSignupTitle) {
          this._originalSignupTitle = titleEl.textContent;
        }
        titleEl.textContent = this.dataset.signupModalTitle;
      }

      $signupModal.classList.add("active");

      this._restoreSignupTitleOnClose($signupModal, titleEl);
    }, 350);
  }

  _restoreSignupTitleOnClose($signupModal, titleEl) {
    if (!this._originalSignupTitle || !titleEl) return;

    const observer = new MutationObserver(() => {
      if (!$signupModal.classList.contains("active")) {
        titleEl.textContent = this._originalSignupTitle;
        this._originalSignupTitle = null;
        observer.disconnect();
      }
    });
    observer.observe($signupModal, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  _cleanupCta() {
    this._cancelCtaTimer();

    if (this._player && this._onEnded) {
      this._player.off("ended", this._onEnded);
      this._onEnded = null;
    }

    if (this._player && this._onTimeUpdate) {
      this._player.off("timeupdate", this._onTimeUpdate);
      this._onTimeUpdate = null;
    }

    if (this._ctaButtonHandler) {
      this._ctaButtonHandler.el.removeEventListener(
        "click",
        this._ctaButtonHandler.fn
      );
      this._ctaButtonHandler = null;
    }

    if (this._ctaCloseHandler) {
      this._ctaCloseHandler.el.removeEventListener(
        "click",
        this._ctaCloseHandler.fn
      );
      this._ctaCloseHandler = null;
    }

    const ctaEl = document.querySelector(".post-video-cta");
    this._hideCta(ctaEl);
    this._ctaVisible = false;
  }

  // ----- Existing methods -----

  _waitForModalIframe() {
    const deadline = performance.now() + 2000;

    const poll = (resolve, reject) => {
      const modalEl = this._findOpenModal();
      const iframe = modalEl?.querySelector('iframe[src*="player.vimeo.com"]');

      if (iframe) return resolve({ iframe, modalEl });
      if (performance.now() > deadline)
        return reject(new Error("Modal iframe not found"));
      requestAnimationFrame(() => poll(resolve, reject));
    };

    return new Promise(poll);
  }

  _findOpenModal() {
    const candidates = Array.from(
      document.querySelectorAll(
        '.modal, [role="dialog"], .Modal, .c-modal, .drawer, .gated-article__signup'
      )
    );

    return (
      candidates.find((el) => {
        const r = el.getBoundingClientRect();
        const visible = r.width > 0 && r.height > 0;
        const style = window.getComputedStyle(el);
        return (
          visible && style.visibility !== "hidden" && style.display !== "none"
        );
      }) || null
    );
  }

  _findCloseButton(modalEl) {
    const selectors = [".modal__close", ".modal"];
    for (const sel of selectors) {
      const btn = modalEl.querySelector(sel);
      if (btn) return btn;
    }
    return null;
  }
}

customElements.define("gated-article", GatedArticle);

class GatedSignup extends HTMLElement {
  constructor() {
    super();
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    this.signup = this.querySelector(".gated-article__signup");
    this.close = this.querySelector(".gated-article__signup-close");

    if (this.close) {
      this.close.addEventListener("click", () => {
        if (this.signup) {
          this.signup.classList.remove("gated-article__signup--active");
        }
        window.removeEventListener("scroll", this.handleScroll);
      });
    }

    if (this.signup) {
      window.addEventListener("scroll", this.handleScroll);
    }
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll() {
    if (window.scrollY >= 100) {
      if (this.signup) {
        this.signup.classList.add("gated-article__signup--active");
      }
    } else {
      if (this.signup) {
        this.signup.classList.remove("gated-article__signup--active");
      }
    }
  }
}

customElements.define("gated-signup", GatedSignup);
